import { AppBar, Menu, Tab } from "@mui/material";
import { StyledComponentProps } from "@mui/styles";
import { FeedClassKey, TabLargeStyle, TimelineClassKey } from "assets/jss/common-styles";
import AdvFilter, { AdvFilterCommand, AdvFilterComponent, AdvFilterPicker, AdvFilterResult } from "components/AdvFilter";
import { CmdList, CmdPoll, CmdPost, CmdPublishEndDate, CmdPublishStartDate, CmdQuestion, CmdWithFile, CmdWithImage, CmdWithVideo, CommandLabels } from "components/AdvFilter/CommonCommands";
import { AppBarTabs } from "components/AppTabs";
import ButtonOrderBy from "components/Buttons/ButtonOrderBy";
import ButtonResetFilter from "components/Buttons/ButtonResetFilter";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import CountFilter from "components/CountFilter";
import { MuiFilterAdvancedContainer, MuiFilterAppBar, MuiFilterLine, MuiFilterToolbar } from "components/MuiExtras/Filter";
import { GridEx } from "components/MuiExtras/Grid/GridEx";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiMenuItemChecked from "components/MuiExtras/MuiMenuItemChecked";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import { FeedType, PublicationType } from "models/ApiEnums";
import { FeedAttachType, FeedBaseOrderBy, FeedContentUpdate, TimelineUserProfileFilter } from "models/ApiModels";
import * as React from "react";
import { useTranslation, WithTranslation, withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { Action } from "redux";
import { ThunkDispatch } from "redux-thunk";
import * as FeedActions from 'store/feed/actions';
import { FeedState } from "store/feed/reducer";
import { ApplicationState } from "store/rootReducer";
import CardManager from "views/Timeline/Components/CardManager";

/////////////////////////////////////////////////////////////////////////////////////////////////

interface IStateProps {
    feed: FeedState;
}

interface IState {
    advFilters: AdvFilterResult[];
    orderByMenuAnchorEl: any;
    dialog: any;
    selectedTab: FeedType;
}

interface IProps {
    userId: string;
    fullView: boolean;
    onRef?: (ref: UserProfilePublicationsComponent) => void;
}


interface IDispatchProps {
    onReloadPublication: (uid: string, publicationId: string) => Promise<void>;
    onRemovePublication?: (uid: string, publicationId: string, publicationType: PublicationType) => Promise<boolean>;
    onExpandPublication: (uid: string, publicationId: string) => Promise<void>;
    onFeedReset: () => void;
    onUpdateFeedData: (newItemDto: FeedContentUpdate) => void;
    onRemoveFromFeed: (uid: string, publicationId: string) => void;
    onGetFeed: (filter: TimelineUserProfileFilter, orderBy: FeedBaseOrderBy, startIndex: number, endIndex: number) => Promise<any>;
}

type Props = IProps & IStateProps & IDispatchProps & StyledComponentProps<TimelineClassKey | FeedClassKey> & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////////////////

interface IRenderMenu {
    category: FeedType;
    onChange: (event: React.ChangeEvent<{}>, value: any) => void;
}

const RenderMenuBar: React.FC<Props & IRenderMenu> = (props) => {
    const { t } = useTranslation();
    return (
        <AppBar position="static" color="default" classes={{ root: props.classes.appTabFilterRoot }}>
            <AppBarTabs
                centered={true}
                value={props.category}
                onChange={props.onChange}
            >
                <Tab
                    label={t("Common.Feed.UserProfile.TabPublications")}
                    value={FeedType.UserProfile}
                    style={TabLargeStyle}
                />
                <Tab
                    label={t("Common.Feed.UserProfile.TabMentions")}
                    value={FeedType.UserProfileMentions}
                    style={TabLargeStyle}
                />
                <Tab
                    label={t("Common.Feed.UserProfile.TabThanks")}
                    value={FeedType.UserProfileThanks}
                    style={TabLargeStyle}
                />
            </AppBarTabs>
        </AppBar>
    );
};

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////


export class UserProfilePublicationsComponent extends React.Component<Props, IState> {

    private advFilterRef: AdvFilterComponent;
    private filter: TimelineUserProfileFilter;
    private orderBy: FeedBaseOrderBy = FeedBaseOrderBy.Default;

    ///
    constructor(props: Props) {
        super(props);

        this.state = {
            advFilters: [],
            selectedTab: FeedType.UserProfile,
            orderByMenuAnchorEl: null,
            dialog: null
        };

        this.filter = this.resetFilter(props);
    }

    ///
    public feedReset() {
        this.props.onFeedReset();
    }

    ///
    private get commands(): AdvFilterCommand[] {
        const cmdLabels = CommandLabels();
        return [
            { keyword: CmdWithVideo, displayName: cmdLabels[CmdWithVideo], group: 2 },
            { keyword: CmdWithImage, displayName: cmdLabels[CmdWithImage], group: 2 },
            { keyword: CmdWithFile, displayName: cmdLabels[CmdWithFile], group: 2 },

            { keyword: CmdPost, displayName: cmdLabels[CmdPost], group: 3 },
            { keyword: CmdQuestion, displayName: cmdLabels[CmdQuestion], group: 3 },
            { keyword: CmdList, displayName: cmdLabels[CmdList], group: 3 },
            { keyword: CmdPoll, displayName: cmdLabels[CmdPoll], group: 3 },

            { keyword: CmdPublishStartDate, displayName: cmdLabels[CmdPublishStartDate], picker: AdvFilterPicker.Calendar, group: 4 },
            { keyword: CmdPublishEndDate, displayName: cmdLabels[CmdPublishEndDate], picker: AdvFilterPicker.Calendar, group: 4 }
        ];
    }

    ///
    private handleTabChange = (event: any, value: any) => {
        this.setState({ selectedTab: value },
            () => {
                this.filter.category = value;
                this.orderBy = value == FeedType.UserProfileThanks ? FeedBaseOrderBy.MostThanks : FeedBaseOrderBy.Default;
                this.props.onFeedReset();
            });
    };

    ///
    private resetFilter(props: Props): TimelineUserProfileFilter {
        return {
            userId: props.userId,
            category: this.state.selectedTab,
            searchText: null,
            startDate: null,
            endDate: null,
            attachTypes: [],
            publicationTypes: [],
        };
    }

    ///
    private isFiltered() {
        return this.filter.searchText != null || this.filter.startDate != null || this.filter.endDate != null || this.filter.attachTypes.length > 0 || this.filter.publicationTypes.length > 0;
    }

    ///
    private applyOrder = (orderBy: FeedBaseOrderBy) => {
        this.orderBy = orderBy;
        this.setState({ orderByMenuAnchorEl: null },
            () => this.feedReset()
        );

    };

    ///
    private buildFilter(searchText: string, advFilters: AdvFilterResult[]): TimelineUserProfileFilter {
        var filter: TimelineUserProfileFilter = {
            userId: this.props.userId,
            category: this.state.selectedTab,
            searchText: searchText.length == 0 ? null : searchText
        };

        filter.publicationTypes = [];
        if (advFilters.findIndex(x => x.keyword == CmdPost) > -1) filter.publicationTypes.push(PublicationType.Post);
        if (advFilters.findIndex(x => x.keyword == CmdList) > -1) filter.publicationTypes.push(PublicationType.List);
        if (advFilters.findIndex(x => x.keyword == CmdQuestion) > -1) filter.publicationTypes.push(PublicationType.Question);
        if (advFilters.findIndex(x => x.keyword == CmdPoll) > -1) filter.publicationTypes.push(PublicationType.Poll);

        filter.attachTypes = [];
        if (advFilters.findIndex(x => x.keyword == CmdWithVideo) > -1) filter.attachTypes.push(FeedAttachType.Video);
        if (advFilters.findIndex(x => x.keyword == CmdWithImage) > -1) filter.attachTypes.push(FeedAttachType.Image);
        if (advFilters.findIndex(x => x.keyword == CmdWithFile) > -1) filter.attachTypes.push(FeedAttachType.File);

        var dt = advFilters.find(x => x.keyword == CmdPublishStartDate);
        filter.startDate = dt ? (dt.value as Date).putStartOfDay() : null;

        dt = advFilters.find(x => x.keyword == CmdPublishEndDate);
        filter.endDate = dt ? (dt.value as Date).putEndOfDay() : null;

        return filter;
    }

    ///
    private handleUpdateFilter = (searchText: string, advFilters: AdvFilterResult[]) => {
        this.filter = (searchText.length == 0 && advFilters.length == 0)
            ? this.resetFilter(this.props)
            : this.buildFilter(searchText, advFilters);

        this.setState({ advFilters }, () => this.feedReset());
    };

    ///
    private renderAdvFilter(): JSX.Element {
        return (
            <MuiFilterAppBar position="static">
                <MuiFilterToolbar variant="dense" >

                    <MuiFilterLine>

                        <RenderMenuBar {...this.props} category={this.state.selectedTab} onChange={this.handleTabChange} />

                    </MuiFilterLine>

                    <GridEx container>
                        <GridEx item xs>
                            <MuiFilterAdvancedContainer>
                                <AdvFilter
                                    id="advfilter"
                                    commands={this.commands}
                                    innerRef={(ref) => this.advFilterRef = ref}
                                    placeHolder={this.props.t("Common.Filter.Adv.InputPlaceholder")}
                                    placeHolderFocus={this.props.t("Common.Filter.Adv.InputPlaceholderFocus")}
                                    title={this.props.t("Common.Filter.Adv.InputTitleEnterToFilter")}
                                    selectedCommands={this.state.advFilters}
                                    onUpdateSelection={this.handleUpdateFilter} />
                            </MuiFilterAdvancedContainer>
                        </GridEx>
                        <GridEx item>
                            <ButtonOrderBy
                                onClick={
                                    (e) => {
                                        this.setState({ orderByMenuAnchorEl: e.currentTarget });
                                    }
                                }
                            />
                            <Menu
                                id="simple-menu"
                                anchorEl={this.state.orderByMenuAnchorEl}
                                open={this.state.orderByMenuAnchorEl != null}
                                onClose={
                                    () => {
                                        this.setState({ orderByMenuAnchorEl: null });
                                    }
                                }
                            >
                                <MuiMenuItemChecked text={this.props.t("Common.Filter.Toolbar.OrderBy.MostRecent")} value={FeedBaseOrderBy.Default} currentSelection={this.orderBy} onClick={this.applyOrder} />
                                <MuiMenuItemChecked text={this.props.t("Common.Filter.Toolbar.OrderBy.MostLiked")} value={FeedBaseOrderBy.Best} currentSelection={this.orderBy} onClick={this.applyOrder} />
                                <MuiMenuItemChecked text={this.props.t("Common.Filter.Toolbar.OrderBy.MostViewed")} value={FeedBaseOrderBy.MostViews} currentSelection={this.orderBy} onClick={this.applyOrder} />
                                <MuiMenuItemChecked text={this.props.t("Common.Filter.Toolbar.OrderBy.MostCommented")} value={FeedBaseOrderBy.MostComments} currentSelection={this.orderBy} onClick={this.applyOrder} />
                                <MuiMenuItemChecked text={this.props.t("Common.Filter.Toolbar.OrderBy.MostThanked")} value={FeedBaseOrderBy.MostThanks} currentSelection={this.orderBy} onClick={this.applyOrder} />
                            </Menu>
                        </GridEx>

                    </GridEx>

                </MuiFilterToolbar>
            </MuiFilterAppBar>
        );
    }

    ///
    public componentDidMount() {
        if (this.props.onRef) this.props.onRef(this);
        this.feedReset();
    }

    ///
    public componentWillUnmount() {
        if (this.props.onRef) this.props.onRef(null);
        this.feedReset();
    }

    ///
    public componentDidUpdate(prevProps: Props) {
        if (prevProps.userId != this.props.userId) {
            this.feedReset();
        }
    }

    ///
    public render() {
        const { classes, feed, feed: { lastError, totalRecords, loading }, snackbar } = this.props;

        return (
            <>
                {this.state.dialog}

                <MuiContainer margin="normal">
                    {this.renderAdvFilter()}
                </MuiContainer>

                {
                    totalRecords > 0 &&
                    <MuiContainer margin="normal">
                        <CountFilter
                            count={totalRecords}
                            filtered={this.isFiltered()}
                            resource="Common.Feed.Filter.PublicationResultText"
                            filteredResource="Common.Feed.Filter.PublicationFilteredResultText"
                            onResetFilter={() => this.advFilterRef.resetFilter()}
                        />
                    </MuiContainer>
                }

                <CardManager
                    classes={classes}
                    feedType={FeedType.UserProfile}
                    feed={feed}
                    snackbar={snackbar}
                    onReloadPublication={this.props.onReloadPublication}
                    onExpandPublication={this.props.onExpandPublication}
                    onUpdatePublication={this.props.onUpdateFeedData}
                    onRemovePublication={this.props.onRemovePublication}
                    onRemoveFromFeed={this.props.onRemoveFromFeed}
                    onGetFeed={(startIndex: number, endIndex: number) => this.props.onGetFeed(this.filter, this.orderBy, startIndex, endIndex)}
                    onFeedReset={() => this.props.onFeedReset()}
                />

                {
                    lastError != null
                        ? <RequestErrorReport error={lastError} />
                        : totalRecords == 0 && !loading && !this.isFiltered()
                            ? (
                                <MuiMessageBox
                                    variant="info"
                                    message={this.props.t("Common.Feed.Filter.PublicationResultText_zero")}
                                />
                            )
                            : totalRecords == 0 && !loading && this.isFiltered()
                                ? (
                                    <MuiMessageBox
                                        variant="warning"
                                        action={<ButtonResetFilter onClick={() => this.advFilterRef.resetFilter()} />}
                                        message={this.props.t("Common.Feed.Filter.PublicationFilteredResultText_zero")}
                                    />
                                )
                                : null
                }
            </>
        );
    }
}

/////////////////////////////////////////////////////////////////////////////////////////////////

function mapStateToProps(state: ApplicationState): IStateProps {
    return {
        feed: state.feedPrivate
    };
}

function mapDispatchToProps(dispatch: ThunkDispatch<ApplicationState, void, Action<any>>, ownProps: IProps): IDispatchProps {
    const action = new FeedActions.UserProfileFeedAction(ownProps.userId);

    return {
        onReloadPublication: (uid, publicationId) => dispatch(action.reloadPublication(uid, publicationId)),
        onExpandPublication: (uid, publicationId) => dispatch(action.getPublicationFullContent(uid, publicationId)),
        onRemovePublication: (uid, publicationId, publicationType) => dispatch(action.removePublication(uid, publicationId, publicationType)),

        onUpdateFeedData: (newItemDto) => dispatch(action.updateFeedData(newItemDto)),
        onRemoveFromFeed: (uid, publicationId) => dispatch(action.removeFeedData(uid, publicationId)),

        onFeedReset: () => dispatch(action.feedReset()),
        onGetFeed: (filter, orderBy, startIndex, endIndex) => dispatch(action.getFeed(filter, orderBy, startIndex, endIndex)),
    };
}

export default connect<IStateProps, IDispatchProps>(mapStateToProps, mapDispatchToProps)(withSnackbar(withTranslation()(UserProfilePublicationsComponent)));
